import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import AddButton from '../core/AddButton';
import CancelButton from '../core/CancelButton';
import LanguageSelector from '../core/LanguageSelector';
import ListResult from '../core/ListResult';
import PrimaryButton from '../core/PrimaryButton';
import { globalStyles as styles } from '../core/style/gstheme';
import TitledPanel from '../core/TitledPanel';
import TitledTextField from '../core/TitledTextField';
import { debounce, isRTL, makeCancelable, sanitiseText, sortByLength } from '../core/utils/utils';
import GS from '../grafsync/grafsync';
import ComponentLink from '../application-core/ComponentLink';
import { MAX_LABEL_LENGTH } from '../core/utils/constants';

class DataLabelDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            acOpen: false,
            checking: false,
            checked: false,
            isDuplicate: false,
            isDeleted: false,
            text: "",
            lang: null
        };
    }

    handleClickOpen = () => {
        const langsToUse = this.computeLangsToUse();
        const selectedLang = this.props.viewLang || this.props.defaultLang;
        // If only one language option select it
        const initialLang = langsToUse.includes(selectedLang) ? selectedLang : (
            langsToUse.length === 1 ? langsToUse[0] : null
        );
        this.setState({
            open: true,
            lang: initialLang
        });
    };

    findResource = (value, lang) => {
        let searchParts = [
            {
                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                value: "https://grafsync.graphifi.com/schema/gs/Resource",
            },
            {
                predicate: "http://www.w3.org/2000/01/rdf-schema#label",
                value: value,
                isExactMatch: false
            }
        ];
        this.doAutocomplete(
            {
                graph: [this.props.graph],
                search: searchParts,
                language: lang,
                predicates: [
                    "http://www.w3.org/2000/01/rdf-schema#label",
                    "https://grafsync.graphifi.com/schema/gs/inDataset",
                    "https://grafsync.graphifi.com/schema/gs/iri",
                    "https://grafsync.graphifi.com/schema/gs/pendingDeletion"
                ]
            })
            .promise
            .then((data) => {
                const sortedResults = data.results && data.results.length > 0 ? sortByLength([...data.results], 'label', lang).slice(0, 5) : null;
                this.setState({
                    results: sortedResults,
                    acOpen: data.results && data.results.length > 0 ? true : false
                });
                // It's a duplicate if there is an equivalent label in the in-context dataset
                const isDuplicate = data.results && data.results.length > 0 ? data.results.some(result => {
                    // We may get an edge case of mutiple labels in a single language
                    if (Array.isArray(result.label[lang])) {
                        return result.label[lang].some(label =>
                            label.toLowerCase() === this.state.label.toLowerCase()
                            && result.inDataset && result.inDataset.includes(this.props.datasetIRI));
                    }
                    return result.label[lang].toLowerCase() === this.state.text.toLowerCase()
                        && result.inDataset && result.inDataset.includes(this.props.datasetIRI);
                }) : false;
                this.setState({ isDeleted: false, isDuplicate: isDuplicate, checked: true });
                this.setState({ checking: false });
            })
            .catch((e) => {
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
    };

    doAutocomplete(payload) {
        this.cancellable = makeCancelable(GS.autocomplete(payload));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    addLabel = async () => {
        this.props.onAddLanguage(this.state.lang, this.state.text);
        this.exitAddLabel();
    };

    exitAddLabel = () => {
        const langsToUse = this.computeLangsToUse();
        this.setState({
            open: false,
            acOpen: false,
            text: "",
            isDuplicate: false,
            isDeleted: false,
            checking: false,
            checked: true,
            lang: this.props.defaultLang && langsToUse.includes(this.props.defaultLang) ? this.props.defaultLang : null
        });
    };

    selectLanguage = (lang) => {
        this.setState({ lang }, () => {
            if (this.state.text !== "") {
                this.setState({ checking: true }, debounce(this.findResource, this, [this.state.text, this.state.lang]));
            }
        });
    };

    handleChange = event => {
        const t = event.target;
        const cleanText = sanitiseText(t.value.trimLeft());
        this.setState({ [t.name]: cleanText, checked: false }, () => {
            const invalid = this.props.existingValues
                && this.props.existingValues[this.state.lang]
                && this.props.existingValues[this.state.lang]
                    .map(value => value.toLowerCase())
                    .includes(t.value.trimLeft().toLowerCase());
            if (!invalid && this.props.checkForLabel && this.state.lang && cleanText !== "") {
                this.setState({ checking: true }, debounce(this.findResource, this, [cleanText, this.state.lang]));
            }
            else {
                this.setState({ checking: false, checked: true, acOpen: false });
            }
        });
    };

    computeLangsToUse() {
        const { onePerLang, existingLangs, langs } = this.props;
        return onePerLang && existingLangs ? langs.filter(lang => !existingLangs.includes(lang)) : langs;
    }

    handleClickAway = () => this.setState({ acOpen: false });

    render() {

        const { existingValues, title, datasetIRI, multiline, existingLangs, onePerLang,
            maxLength, classes, name, defaultLang, placeholder, graph } = this.props;
        const { text, lang, checking, checked, isDeleted, acOpen, results } = this.state;
        const langsToUse = this.computeLangsToUse();
        const langFields = existingValues && existingValues[lang] && lang ?
            (Array.isArray(existingValues[lang]) ?
                existingValues[lang]
                :
                [existingValues[lang]]
            )
            : null;
        const existingOnePerLang = existingLangs && onePerLang && existingLangs.includes(lang);

        const lowerText = text.toLowerCase();
        const existing = langFields ? langFields.map(value => value.toLowerCase()).includes(lowerText.trim()) : null;
        const invalid = text !== "" && (existing || checking || !checked || isDeleted || existingOnePerLang);
        const rtl = isRTL(lang);

        return (
            <React.Fragment>
                <AddButton dataTest="add-language-value" onClick={this.handleClickOpen} title="Add a language value" />
                <Dialog
                    data-test="add-language-field"
                    maxWidth="md"
                    fullWidth
                    classes={{
                        paper: classes.paper,
                        paperWidthMd: classes.largeDialog
                    }}
                    className={classes.dialog}
                    open={this.state.open}
                    onClose={this.exitAddLabel}
                    aria-labelledby="text-dialog-title">
                    <DialogTitle data-test="add-language-field-title"
                        disableTypography className={classes.mainHeading} id="text-dialog-title">{title ? title : 'Add Label'}</DialogTitle>
                    <DialogContent style={{
                        height: '230px'
                    }}>
                        <TitledPanel title="Language">
                            <LanguageSelector
                                includeLabel={false}
                                selectedLang={lang}
                                langs={langsToUse}
                                onSelect={this.selectLanguage}
                                defaultLang={defaultLang} />
                        </TitledPanel>
                        <form autoComplete="off" noValidate="novalidate">
                            <TitledTextField
                                dataTest="language-value"
                                name="text"
                                title={name || "Text"}
                                autoFocus={lang !== null}
                                required={true}
                                text={text}
                                placeholder={placeholder || "Text"}
                                margin={true}
                                multiline={multiline}
                                maxLength={maxLength || 500}
                                lang={lang}
                                error={existing || existingOnePerLang}
                                errorText={existing ? "This value already exists on this resource in this language."
                                    :
                                    existingOnePerLang ?
                                        "This language has been added already."
                                        :
                                        null
                                }
                                onEnter={checked ? this.addLabel : null}
                                onChange={this.handleChange} />
                            {acOpen && results && results.length > 0 &&
                                <ClickAwayListener onClickAway={this.handleClickAway}>
                                    <List
                                        data-test="list-results"
                                        dense
                                        className={classes.existingResults}
                                        subheader={<ListSubheader
                                            data-test="list-sub-header"
                                            style={{ ...(rtl) && { textAlign: 'right' } }}
                                            className={classes.existingResultsHeader}>Existing Resources</ListSubheader>}
                                        style={{ marginTop: -8 }}>
                                        {results.map((result, i) => {
                                            let label = Array.isArray(result.label[lang]) ?
                                                result.label[lang].sort().join(" AND ")
                                                :
                                                result.label[lang];
                                            let additionalLabel = null;
                                            if (result.inDataset && !result.inDataset.includes(datasetIRI)) {
                                                additionalLabel = <div>
                                                    {result.inDataset.map(dataset =>
                                                        <ComponentLink
                                                            margin={true}
                                                            key={dataset}
                                                            id={dataset}
                                                            handleNotAvailable={true}
                                                            graph={graph}
                                                            defaultLang={defaultLang} />
                                                    )}
                                                </div>;
                                            }
                                            const currentIRI = result.iri && result.iri.length > 0 ?
                                                result.iri[result.iri.length - 1]
                                                :
                                                result.id;

                                            return <ListResult
                                                key={i}
                                                text={label}
                                                iri={currentIRI}
                                                lang={lang}
                                                pendingDeletion={result.pendingDeletion}
                                                invalid={true}
                                                entailed={result.isEntailed}
                                                additionalLabel={additionalLabel}
                                            />;
                                        })}
                                    </List>
                                </ClickAwayListener>
                            }
                        </form>
                    </DialogContent>
                    <DialogActions className={classes.dialogActions}>
                        <CancelButton onClick={this.exitAddLabel} />
                        <PrimaryButton
                            disabled={lang === null || text.length === 0 || invalid || checking}
                            onClick={this.addLabel}>Add</PrimaryButton>
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }
}

DataLabelDialog.defaultProps = {
    onePerLang: false,
    checkForLabel: true,
    maxLength: MAX_LABEL_LENGTH
};

DataLabelDialog.propTypes = {
    maxLength: PropTypes.number,
    graph: PropTypes.string.isRequired,
    onAddLanguage: PropTypes.func.isRequired,
    datasetIRI: PropTypes.string,
    existingValues: PropTypes.object,
    name: PropTypes.string,
    onePerLang: PropTypes.bool,
    placeholder: PropTypes.string,
    defaultLang: PropTypes.string.isRequired,
    viewLang: PropTypes.string,
    title: PropTypes.string.isRequired
};

export default withStyles(styles)(DataLabelDialog);